import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Target, Eye, Scale, Sparkles, Phone, ArrowRight } from 'lucide-react';

export const ChiSiamo = () => {
  const values = [
    {
      icon: Target,
      title: 'Missione',
      description:
        'Promuoviamo il recupero e la reintroduzione di metalli e polimeri da cavi elettrici nei cicli produttivi, secondo i principi dell\'economia circolare, riducendo al minimo l\'impatto ambientale.',
    },
    {
      icon: Eye,
      title: 'Visione',
      description:
        'Sviluppiamo continuamente tecnologie e processi capaci di valorizzare sia la componente metallica che polimerica. Puntiamo alle certificazioni ISO 14001, EuCertPlast ed End of Waste, distinguendoci per competenza tecnica, trasparenza e innovazione.',
    },
    {
      icon: Scale,
      title: 'Valori',
      description:
        'Sostenibilità ambientale, economia circolare, qualità dei materiali recuperati, trasparenza nelle relazioni commerciali e impegno concreto verso un futuro più verde.',
    },
  ];

  const distingue = [
    {
      emoji: '🌱',
      title: 'Specializzazione esclusiva sui cavi elettrici',
      description: 'Conosciamo a fondo il materiale e offriamo soluzioni mirate',
    },
    {
      emoji: '🔧',
      title: 'Processo certificato e trasparente',
      description: 'Separazione completa di rame e polimeri con rese verificabili',
    },
    {
      emoji: '♻️',
      title: 'Approccio circolare integrato',
      description: 'Valorizziamo ogni componente, riducendo al minimo gli scarti',
    },
    {
      emoji: '✨',
      title: 'Qualità certificata dei materiali recuperati',
      description: 'Granulato di rame al 99% di purezza e polimeri di prima scelta',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section Premium */}
      <section className="relative py-24 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        </div>
        <div className="absolute inset-0 pattern-dots opacity-20"></div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-semibold">Dal 2025</span>
            </div>
            <h1 className="text-6xl font-bold mb-6 animate-fade-in">Chi Siamo</h1>
            <p className="text-xl text-white/90 animate-fade-in leading-relaxed" style={{ animationDelay: '0.1s' }}>
              Un'azienda familiare con la passione per la sostenibilità e l'innovazione nel settore del recupero materiali
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-gradient-to-b from-white to-[var(--warm-gray)]">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold mb-8">La Nostra Storia</h2>
              <div className="space-y-6 text-lg text-[var(--text-light)] leading-relaxed">
                <p>
                  <strong className="text-[var(--copper-primary)] font-semibold">FIRM s.a.s.</strong> nasce nel 2025 come realtà familiare
                  specializzata nel riciclo di cavi elettrici, con l'obiettivo di contribuire attivamente all'economia circolare.
                </p>
                <p>
                  La nostra visione è chiara: trasformare quello che per molti è un rifiuto in una risorsa preziosa.
                  Grazie a un processo produttivo efficiente, separiamo il rame dalla guaina isolante dei cavi elettrici, ottenendo
                  granulato di rame di alta qualità e materiali polimerici recuperabili.
                </p>
                <p>
                  Operiamo con <strong className="text-[var(--verdigris)] font-semibold">autorizzazione R13 "messa in riserva"</strong>, nel pieno rispetto delle normative ambientali,
                  garantendo ai nostri clienti professionalità, trasparenza e materiali certificati.
                </p>
                <p>
                  Investiamo costantemente in tecnologie e competenze per migliorare i nostri processi,
                  ampliare la gamma di servizi e crescere insieme ai nostri partner.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-video bg-gradient-to-br from-[var(--warm-gray)] to-white rounded-3xl overflow-hidden border-4 border-[var(--copper-primary)] shadow-2xl hover:shadow-copper transition-all duration-500 hover:-translate-y-2">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                >
                  <source
                    src="https://customer-assets.emergentagent.com/job_copper-recovery/artifacts/uvyom94z_creati_20260108000338_ee476ee4603f4983b7ac2a1268f879e7.mp4"
                    type="video/mp4"
                  />
                  Il tuo browser non supporta il tag video.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cosa ci Distingue Section */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Cosa ci Distingue</h2>
            <p className="text-xl text-[var(--text-light)] max-w-3xl mx-auto leading-relaxed">
              Nel settore del recupero materiali, ci differenziamo per:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {distingue.map((item, index) => (
              <Card
                key={index}
                className="group bg-white border-2 border-gray-200 hover:border-[var(--copper-primary)] transition-all duration-500 hover:shadow-xl hover:-translate-y-2"
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <span className="text-3xl">{item.emoji}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 group-hover:text-[var(--copper-primary)] transition-colors">{item.title}</h3>
                      <p className="text-[var(--text-light)] leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gradient-to-b from-[var(--warm-gray)] to-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Missione, Visione e Valori</h2>
            <p className="text-xl text-[var(--text-light)] max-w-3xl mx-auto leading-relaxed">
              I principi che guidano il nostro lavoro quotidiano
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={index}
                  className="group bg-white border-2 border-gray-200 hover:border-[var(--verdigris)] transition-all duration-500 hover:shadow-xl hover:-translate-y-2"
                >
                  <CardContent className="p-8">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 mb-6">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 group-hover:text-[var(--verdigris)] transition-colors">{value.title}</h3>
                    <p className="text-[var(--text-light)] leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold mb-6">Il Nostro Impegno</h2>
              <p className="text-xl text-[var(--text-light)] leading-relaxed">
                Generiamo valore condiviso attraverso tre pilastri fondamentali
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="text-center p-8 border-2 hover:border-[var(--copper-primary)] transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                <CardContent className="p-0">
                  <div className="w-24 h-24 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-4xl">🌍</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Tutela Ambientale</h3>
                  <p className="text-[var(--text-light)] leading-relaxed">
                    Pratiche di riciclo efficienti per ridurre l'impatto ambientale e preservare le risorse naturali
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center p-8 border-2 hover:border-[var(--verdigris)] transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                <CardContent className="p-0">
                  <div className="w-24 h-24 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-4xl">📈</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Competitività Sostenibile</h3>
                  <p className="text-[var(--text-light)] leading-relaxed">
                    Crescita in un mercato che premia l'innovazione e la responsabilità ambientale
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center p-8 border-2 hover:border-[var(--copper-primary)] transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                <CardContent className="p-0">
                  <div className="w-24 h-24 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--verdigris)] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-4xl">♻️</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Valorizzazione dei Materiali</h3>
                  <p className="text-[var(--text-light)] leading-relaxed">
                    Selezione e trattamento dei cavi elettrici per recuperare materiali strategici come rame e polimeri
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Contattaci Ora */}
      <section className="py-24 bg-gradient-to-b from-[var(--warm-gray)] to-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <Card className="bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white border-0 overflow-hidden hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-10 md:p-12 text-center">
                <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-8 mx-auto">
                  <Phone className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-4xl font-bold mb-6">Contattaci Ora</h2>
                <p className="text-xl text-white/90 leading-relaxed mb-10 max-w-2xl mx-auto">
                  Il nostro team è pronto ad aiutarti. Contattaci oggi stesso per discutere delle tue esigenze.
                </p>
                <Link to="/contatti">
                  <Button
                    size="lg"
                    className="bg-white text-[var(--verdigris)] hover:bg-gray-100 px-10 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  >
                    Contattaci Ora
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
